<template>
  <div class="profile-dropdown-wrapper" :class="{ 'mr-3': !sheet }">
    <VaButton
      v-if="sheet"
      preset="secondary"
      color="textPrimary"
      round
      border-color="primary"
      :aria-label="t('user.account')"
      :aria-expanded="open"
      @click="open = true"
    >
      <span class="profile-dropdown__anchor min-w-max">
        <VaIcon aria-hidden="true" name="person" size="24px" color="primary" />
      </span>
    </VaButton>
    <VaDropdown v-else v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" round border-color="primary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaIcon aria-hidden="true" name="person" size="24px" color="primary" />
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in visibleOptions" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="item.action"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>

    <Teleport to="body">
      <div v-if="sheet && open" class="space-sheet">
        <button type="button" class="space-sheet__backdrop" aria-label="Cerrar" @click="open = false" />
        <div class="space-sheet__panel" role="dialog" aria-modal="true" :aria-label="t('user.account')" style="--space-accent: #0b193d">
          <div class="space-sheet__handle" />
          <h2 class="space-sheet__title">{{ t('user.account') }}</h2>
          <template v-for="group in visibleOptions" :key="group.name || 'actions'">
            <button
              v-for="item in group.list"
              :key="item.name"
              type="button"
              class="space-sheet__row"
              :class="{ 'space-sheet__create': item.name === 'logout' }"
              @click="onSheetItem(item)"
            >
              <span class="space-sheet__icon">
                <VaIcon :name="item.icon" size="22px" color="#0b193d" />
              </span>
              <span class="space-sheet__name">{{ t(`user.${item.name}`) }}</span>
            </button>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useFirebaseStore } from '../../../../stores/firebase'
import { useUserStore } from '../../../../stores/user'
const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action: (() => void | Promise<void>) | null
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{
    sheet?: boolean
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
            action: null,
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
            action: null,
          },
          {
            name: 'tax',
            to: 'tax',
            icon: 'receipt_long',
            action: null,
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
            action: async () => {
              const firebaseX = useFirebaseStore()
              await firebaseX.logout()
              window.location.reload()
            },
          },
        ],
      },
    ],
  },
)

const visibleOptions = computed(() => {
  const showSettings = userStore.isBusinessWorkspace && userStore.billingEnabled
  return props.options
    .map((group) => ({
      ...group,
      list: group.list.filter((item) => item.name !== 'settings' || showSettings),
    }))
    .filter((group) => group.list.length)
})

const isShown = ref(false)
const open = ref(false)

async function onSheetItem(item: ProfileListItem) {
  open.value = false
  if (typeof item.action === 'function') {
    await item.action()
    return
  }
  if (item.to) router.push({ name: item.to })
}

watch(open, (value) => {
  document.body.style.overflow = value ? 'hidden' : ''
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
